import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rp, statusColor } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AccountLinkQueue } from "@/admin/AccountLinkQueue";
import { MembershipPanel } from "@/admin/MembershipPanel";
import { AddCustomerDialog } from "@/admin/AddCustomerDialog";
import { HistoricalRentals } from "@/admin/HistoricalRentals";

const TAGS = ["New Customer", "Returning Customer", "VIP", "Community", "Cafe", "Corporate", "Event Organizer", "Partner", "Blacklist"];

const TIER_STYLE = {
  Bronze: "bg-amber-100 text-amber-900 border-amber-300",
  Silver: "bg-slate-100 text-slate-700 border-slate-300",
  Gold: "bg-yellow-100 text-yellow-800 border-yellow-400",
  Platinum: "bg-cyan-50 text-cyan-800 border-cyan-300",
  Diamond: "bg-violet-50 text-violet-800 border-violet-300",
};

export const TierBadge = ({ tier, percent }) => (
  <span data-testid={`tier-badge-${tier || "none"}`}
    className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${TIER_STYLE[tier] || "bg-black/5 border-black/10 text-ink-soft"}`}>
    {tier || "—"}{percent > 0 ? ` · ${percent}%` : ""}
  </span>
);

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const load = useCallback((query = "") => api.get("/customers", {
    params: { q: query || undefined },
  }).then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  const open = (id) => api.get(`/customers/${id}`).then((r) => setDetail(r.data));
  const toggleTag = async (tag) => {
    const cur = detail.tags || [];
    const next = cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag];
    await api.put(`/customers/${detail.id}`, { tags: next });
    setDetail({ ...detail, tags: next });
    load(q);
  };

  return (
    <div data-testid="admin-customers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl tracking-tight">Customers</h1>
        <AddCustomerDialog onCreated={() => load(q)} />
      </div>
      <AccountLinkQueue />
      <div className="mt-5 flex gap-2">
        <Input data-testid="customer-search" className="max-w-xs" placeholder="Search name or WhatsApp" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} />
        <button onClick={() => load(q)} className="h-10 px-4 rounded-md border border-black/15 text-sm">Search</button>
      </div>
      <div className="mt-5 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Name</th><th className="p-3">WhatsApp</th><th className="p-3">Type</th><th className="p-3">Tier</th><th className="p-3">Rentals</th>
              <th className="p-3">Spending</th><th className="p-3">Tags</th><th className="p-3">Joined</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((c) => (
              <tr key={c.id} className="hover:bg-cream-dark/40 cursor-pointer" data-testid={`customer-row-${c.id}`} onClick={() => open(c.id)}>
                <td className="p-3 text-lm-red">{c.name}</td><td className="p-3">{c.whatsapp}</td><td className="p-3">{c.customer_type}</td>
                <td className="p-3"><TierBadge tier={c.loyalty_tier} percent={c.loyalty_discount_percent} /></td>
                <td className="p-3">{c.total_rentals}</td><td className="p-3">{rp(c.total_spending)}</td>
                <td className="p-3 text-xs">{(c.tags || []).join(", ")}</td><td className="p-3">{(c.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-5 text-ink-soft">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid sm:grid-cols-2 gap-2">
                <div><span className="text-ink-soft">WhatsApp:</span> {detail.whatsapp}</div>
                <div><span className="text-ink-soft">Instagram:</span> {detail.instagram || "—"}</div>
                <div><span className="text-ink-soft">Email:</span> {detail.email || "—"}</div>
                <div><span className="text-ink-soft">Customer ID:</span> {detail.customer_no || "—"}</div>
                <div><span className="text-ink-soft">Akun Member:</span> {detail.member_account_id ? "Terhubung" : "Belum"}</div>
                <div><span className="text-ink-soft">Completed Rentals:</span> {detail.completed_rentals ?? 0}</div>
                <div><span className="text-ink-soft">Type:</span> {detail.customer_type}</div>
                <div><span className="text-ink-soft">Total Rentals:</span> {detail.total_rentals}</div>
                <div className="flex items-center gap-2"><span className="text-ink-soft">Tier:</span>
                  <TierBadge tier={detail.loyalty_tier} percent={detail.loyalty_discount_percent} /></div>
                <div><span className="text-ink-soft">Total Spending:</span> {rp(detail.total_spending)}</div>
                <div><span className="text-ink-soft">Total Discounts:</span> {rp(detail.total_discounts)}</div>
                <div><span className="text-ink-soft">First / Last:</span> {detail.first_rental || "—"} / {detail.last_rental || "—"}</div>
                <div className="sm:col-span-2"><span className="text-ink-soft">Address:</span> {detail.address || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-2">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {TAGS.map((t) => (
                    <button key={t} data-testid={`tag-${t}`} onClick={() => toggleTag(t)}
                      className={`text-xs px-3 py-1 rounded-full border ${(detail.tags || []).includes(t) ? "bg-ink text-cream border-ink" : "border-black/15"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <MembershipPanel customerId={detail.id} />
              <HistoricalRentals customerId={detail.id} onChanged={load} />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-2">Rental History</div>
                <div className="divide-y divide-black/5">
                  {(detail.bookings || []).map((b) => (
                    <Link key={b.id} to={`/admin/bookings/${b.id}`} onClick={() => setDetail(null)} className="flex justify-between gap-3 py-2 hover:text-lm-red">
                      <span>{b.rental_date} · {b.booking_code} · {b.package_name_snapshot} · {b.quantity} unit</span>
                      <span className="flex items-center gap-2">{rp(b.final_rental_total)}
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusColor(b.booking_status)}`}>{b.booking_status}</span></span>
                    </Link>
                  ))}
                  {(detail.bookings || []).length === 0 && <div className="py-2 text-ink-soft">No bookings.</div>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
